import type { DataUIPart } from "ai";

import type { NoxusDataTypes } from "./noxus-message-types";

type ReplayPart = { type: string; text?: string; state?: string };
type ReplayMessage = { role: string; parts: ReplayPart[] };

/**
 * The run ended without a `<final_answer>` marker, so the whole reply already
 * streamed as reasoning. The backend cannot recall those deltas, so it sends
 * the answer back as a transient and the client moves it: subtract it from the
 * trailing reasoning part and render it as the message body.
 */
export function applyFinalAnswerReplay<T>(messages: T[], text: string): T[] {
  const answer = text.trim();
  if (!answer) return messages;

  const index = findLastAssistantIndex(messages as ReplayMessage[]);
  if (index < 0) return messages;

  const message = messages[index] as ReplayMessage;
  const parts = message.parts ?? [];
  const target = findLastIndex(
    parts,
    (p) => p.type === "reasoning" && (p.text ?? "").includes(answer)
  );

  const repaired: ReplayPart[] = parts.map((part, i) => {
    if (i !== target) return part;
    return { ...part, text: (part.text ?? "").replace(answer, "").trimEnd() };
  });
  const kept = repaired.filter(
    (part, i) => i !== target || (part.text ?? "").trim() !== ""
  );

  const next = [...messages];
  next[index] = {
    ...message,
    parts: [...kept, { type: "text", text: answer, state: "done" }],
  } as T;
  return next;
}

export function createFinalAnswerReplay() {
  let pending: string | null = null;
  return {
    onData(part: DataUIPart<NoxusDataTypes>) {
      if (part.type === "data-final_answer_replay") {
        pending = part.data.text || null;
      }
    },
    onFinish<T>(setMessages: (update: (current: T[]) => T[]) => void) {
      const text = pending;
      pending = null;
      if (text) {
        setMessages((current) => applyFinalAnswerReplay(current, text));
      }
    },
  };
}

function findLastAssistantIndex(messages: ReplayMessage[]): number {
  return findLastIndex(messages, (m) => m?.role === "assistant");
}

function findLastIndex<T>(items: T[], match: (item: T) => boolean): number {
  for (let i = items.length - 1; i >= 0; i--) {
    if (match(items[i])) return i;
  }
  return -1;
}
